import { intentHasDeclaredGridConnection } from '../intent/gridScope';
import type { SiteEntity } from '../../types/site';
import type {
  PhasePowerComponentKey,
  PowerAvailabilityPreferences,
  PowerSourceKey,
  ProjectIntent,
  ProjectPhase,
} from '../../types/siteRag';

export type GenerationImpactTestFitApplicabilityStatus =
  | 'required'
  | 'grid-only'
  | 'undecided'
  | 'conflict';

export interface GenerationImpactTestFitScope {
  projectIntent?: Pick<ProjectIntent, 'nuclearInterest' | 'phases'> | null;
  powerAvailabilityPreferences?: Pick<PowerAvailabilityPreferences, 'btmStrategy'> | null;
}

export interface GenerationImpactTestFitApplicability {
  status: GenerationImpactTestFitApplicabilityStatus;
  /**
   * True whenever the declared project includes an on-site generation or
   * storage path. Conflicts remain required so resolving the contradiction
   * cannot accidentally remove the physical-layout evidence gate.
   */
  required: boolean;
  hasOnSitePhase: boolean;
  hasGridPhase: boolean;
  hasNuclearPhase: boolean;
  hasExplicitGridOnlyPhases: boolean;
  /**
   * Whether the accountable grid/BTM strategy field has been explicitly
   * selected. Physical phase content can independently make Test Fit required,
   * but it does not silently answer this Define-stage decision.
   */
  strategyDeclared: boolean;
  btmStrategy: PowerAvailabilityPreferences['btmStrategy'] | null;
  nuclearInterest: ProjectIntent['nuclearInterest'] | null;
  reasons: readonly string[];
}

const ON_SITE_COMPONENTS: ReadonlySet<PhasePowerComponentKey> = new Set([
  'recip',
  'gas-turbine',
  'battery',
  'solar-bess',
  'wind-bess',
  'smr',
  'large-nuclear',
]);

const ON_SITE_SOURCES: ReadonlySet<PowerSourceKey> = new Set([
  'gas-cc',
  'gas-sc',
  'smr',
  'large-nuclear',
  'solar-bess',
  'wind-bess',
  'hybrid',
]);

type PhasePowerPosture = 'on-site' | 'grid-only' | 'undecided';

function phasePowerPosture(phase: ProjectPhase): PhasePowerPosture {
  const configured = phase.powerSourceConfig?.components ?? [];
  // A populated granular mix supersedes the coarse mirror. This avoids
  // treating a stale `powerSource` value as physical project intent.
  if (configured.length > 0) {
    if (configured.some(component => ON_SITE_COMPONENTS.has(component))) return 'on-site';
    return configured.every(component => component === 'grid') ? 'grid-only' : 'undecided';
  }
  if (ON_SITE_SOURCES.has(phase.powerSource)) return 'on-site';
  if (phase.powerSource === 'grid') return 'grid-only';
  return 'undecided';
}

function phaseHasGrid(phase: ProjectPhase): boolean {
  const configured = phase.powerSourceConfig?.components ?? [];
  return configured.length > 0
    ? configured.includes('grid')
    : phase.powerSource === 'grid';
}

function phaseHasNuclear(phase: ProjectPhase): boolean {
  const configured = phase.powerSourceConfig?.components ?? [];
  return configured.length > 0
    ? configured.includes('smr') || configured.includes('large-nuclear')
    : phase.powerSource === 'smr' || phase.powerSource === 'large-nuclear';
}

/**
 * Resolve the on-site-generation Test Fit rule from canonical project intent.
 *
 * This deliberately does not synthesize a power source for legacy single-
 * phase intent: absence is an unresolved decision, not evidence of grid-only
 * or on-site supply. Report planning can therefore ask the user to decide
 * without prematurely activating the Generation & Impact dependency.
 */
export function assessGenerationImpactTestFitApplicability(
  scope: GenerationImpactTestFitScope,
): GenerationImpactTestFitApplicability {
  const intent = scope.projectIntent ?? null;
  const phases = intent?.phases ?? [];
  const postures = phases.map(phasePowerPosture);
  const hasOnSitePhase = postures.includes('on-site');
  const hasGridPhase = phases.some(phaseHasGrid);
  const hasNuclearPhase = phases.some(phaseHasNuclear);
  const hasExplicitGridOnlyPhases = postures.length > 0
    && postures.every(posture => posture === 'grid-only');
  const btmStrategy = scope.powerAvailabilityPreferences?.btmStrategy ?? null;
  const strategyDeclared = btmStrategy != null;
  const nuclearInterest = intent?.nuclearInterest ?? null;
  const hasNuclearIntent = nuclearInterest === 'primary' || nuclearInterest === 'considering';
  const hasBtmIntent = btmStrategy === 'btm-only' || btmStrategy === 'hybrid' || btmStrategy === 'islanded';
  const required = hasOnSitePhase || hasNuclearIntent || hasBtmIntent;
  const reasons: string[] = [];

  if (hasOnSitePhase) reasons.push('At least one project phase includes physical on-site generation or storage.');
  if (hasNuclearIntent) reasons.push(`Project Intent records nuclear as ${nuclearInterest}.`);
  if (hasBtmIntent) reasons.push(`The power strategy is ${btmStrategy}.`);

  const conflicts = [
    ...(btmStrategy === 'grid-only' && (hasOnSitePhase || hasNuclearIntent)
      ? ['The grid-only strategy conflicts with the declared on-site generation path.']
      : []),
    ...(((btmStrategy === 'btm-only' && hasGridPhase) || (btmStrategy === 'islanded' && intentHasDeclaredGridConnection(intent)))
      ? [`The ${btmStrategy === 'islanded' ? 'islanded' : 'BTM-only'} strategy conflicts with a phase that includes grid supply.`]
      : []),
    ...(nuclearInterest === 'no' && hasNuclearPhase
      ? ['Project Intent rules out nuclear while a project phase includes nuclear generation.']
      : []),
  ];
  if (conflicts.length > 0) {
    reasons.push(...conflicts);
    return {
      status: 'conflict',
      required: true,
      hasOnSitePhase,
      hasGridPhase,
      hasNuclearPhase,
      hasExplicitGridOnlyPhases,
      strategyDeclared,
      btmStrategy,
      nuclearInterest,
      reasons,
    };
  }

  if (required) {
    return {
      status: 'required',
      required: true,
      hasOnSitePhase,
      hasGridPhase,
      hasNuclearPhase,
      hasExplicitGridOnlyPhases,
      strategyDeclared,
      btmStrategy,
      nuclearInterest,
      reasons,
    };
  }

  // `grid-only` is a DECLARED state — it is reached ONLY through the
  // accountable `btmStrategy` field, never inferred from phase content.
  //
  // This used to also resolve grid-only when every phase was coded
  // `powerSource: 'grid'` with the strategy still undeclared. That inference
  // silently answered the Define-stage decision this module's own contract
  // says physical phase content must not answer (see `strategyDeclared`), and
  // it had a live consequence: every consumer of `status === 'grid-only'`
  // treats it as "the site DECLARES a grid-only path" — the requirement engine
  // drops Generation & Impact from the required set, the composite's source
  // floor therefore resolves a red Generation & Impact report as merely
  // mitigable, and the Test Fit projection prints "Project Intent declares a
  // grid-only power path". So a RED Generation & Impact report on a site whose
  // owner had declared NOTHING lost its NO-GO authority on every portfolio
  // surface, while the Decision Guide withheld the call (PATH 1b) for exactly
  // that undeclared strategy. Undeclared is the DEFAULT state of a site, and a
  // report must never lose decline authority through a state nobody chose.
  //
  // Grid-coded phases with no declared strategy now read `undecided`:
  // Generation & Impact stays required (and blocked on a promoted scenario)
  // until the owner declares grid-only — at which point it becomes
  // not-applicable — or hybrid / BTM-only.
  if (btmStrategy === 'grid-only') {
    return {
      status: 'grid-only',
      required: false,
      hasOnSitePhase: false,
      hasGridPhase,
      hasNuclearPhase,
      hasExplicitGridOnlyPhases,
      strategyDeclared,
      btmStrategy,
      nuclearInterest,
      reasons: ['The declared power path is grid-only and contains no on-site component.'],
    };
  }

  return {
    status: 'undecided',
    required: false,
    hasOnSitePhase: false,
    hasGridPhase,
    hasNuclearPhase,
    hasExplicitGridOnlyPhases,
    strategyDeclared,
    btmStrategy,
    nuclearInterest,
    reasons: [
      hasExplicitGridOnlyPhases
        ? 'Every declared phase is grid-supplied, but the accountable grid-versus-on-site power strategy has not been declared.'
        : 'Grid versus on-site generation has not been decided.',
    ],
  };
}

export function requiresGenerationImpactTestFit(
  scope: GenerationImpactTestFitScope,
): boolean {
  return assessGenerationImpactTestFitApplicability(scope).required;
}

/** Adapter used by UI, report planning, and generation backstops. */
export function generationImpactTestFitScopeFromSite(
  site: Pick<SiteEntity, 'projectIntent' | 'typedPreferencesDefaults'>,
): GenerationImpactTestFitScope {
  return {
    projectIntent: site.projectIntent,
    powerAvailabilityPreferences: site.typedPreferencesDefaults?.['power-availability'],
  };
}
